<template>
  <div class="passcode">
    <template v-if="showContent">
      <form v-on:submit.prevent="validatePasscode">
        <div class="passcode-copy">
          {{ translations.passcodeDescr }}
        </div>
        <input
          :class="['passcode-input', {
            'passcode-input--error': hasError,
          }]"
          type="password"
          name="passcode"
          v-model="passcode"
          ref="passcodeInput"
          @keydown.space.prevent
          :disabled="disablePasscodeInput"
        />
        <div v-show="hasError" class="passcode-error-message">
          <div v-if="isCampaignInvalid || isCampaignExpired" class="passcode-error-message-campaign-expired">{{ translations.passcodeWCampExpired }}</div>
          <div v-else-if="isPasscodeInvalid" class="passcode-error-message-invalid-passcode">{{ translations.passcodeWPassInvalid }}</div>
        </div>
        <button
          type="submit"
          :class="['button', 'passcode-cta-button', isValidatingPasscode ? 'passcode-cta-button--disabled' : 'primary']"
          :disabled="disableContinueButton"
        >
          <span v-show="!isValidatingPasscode">
            <span class="passcode-cta-button-label-text">
              {{ translations.passcodeContinue }}
            </span>
            <span
              class="icon icon-arrow-right-indicator-open passcode-cta-button-icon"
            ></span>
          </span>
          <base-spinner
            v-show="isValidatingPasscode"
            size="16"
            position="inherit"
            class="passcode-cta-button-spinner"
          ></base-spinner>
        </button>
      </form>
      <div class="passcode-go-to-customs-home-link">
        <a :href="customsHomeUrl">
          {{ translations.passcodeUrl }}
          <span
            class="passcode-go-to-customs-home-link-url"
            >
              {{ displayableCustomsHomeUrl }}
            </span
          >
        </a>
      </div>
    </template>
    <template v-else>
      <base-spinner position="inherit"></base-spinner>
    </template>
  </div>
</template>

<script>
import baseSpinner from '@/components/Spinner';

export default {
  name: "passcode",
  components: {
    baseSpinner
  },
  data() {
    return {
      passcode: "",
      isCampaignInvalid: null,
      isPasscodeInvalid: null,
      isValidatingPasscode: false,
      isValidatingCampaign: true,
      isVerifyingCookie: true,
      isCampaignExpired: false,
    };
  },
  computed: {
    translations(){
      return this.$store.state.translations.data;
    },
    showContent() {
      return !this.isValidatingCampaign && !this.isVerifyingCookie;
    },
    campaignId() {
      return this.$store.state.preloader.passcode.campaignId;
    },
    hasError() {
      return this.isCampaignInvalid || this.isPasscodeInvalid || this.isCampaignExpired;
    },
    disablePasscodeInput() {
      return this.isCampaignInvalid || this.isValidatingPasscode;
    },
    disableContinueButton() {
      return this.passcode.length === 0 || this.isCampaignInvalid || this.isValidatingPasscode;
    },
    customsHomeUrl() {
      return this.translations.passcodeCustomsPath;
    },
    displayableCustomsHomeUrl() {
      // removes the http:// or https:// from the url
      const modifiedUrl = /https?:\/\/(.*)/ig.exec(this.customsHomeUrl);
      return modifiedUrl ? modifiedUrl[1] : null;
    },
  },
  methods: {
    async initValidation() {
      let isPasscodeValid = false;
      let isCampaignValid = false;
      try {
        isPasscodeValid = await this.verifyCookie();
        if (!isPasscodeValid) isCampaignValid = await this.validateCampaignId();
        if (isCampaignValid && this.$refs.passcodeInput) this.$refs.passcodeInput.focus();
      } catch (err) {
        this.isVerifyingCookie = false;
        this.isValidatingCampaign = false;
        console.error(err);
      }
    },

    verifyCookie() {
      return new Promise((resolve, reject) => {
        const passcodeCookie = this.$store.getters['preloader/passcode/getCookieByCampaignId'](this.campaignId);
        this.isVerifyingCookie = true;
        if (passcodeCookie) {
          this.$store
            .dispatch(
              "preloader/passcode/VALIDATE_PASSCODE",
              { passcode: passcodeCookie }
            )
            .then(({ isPasscodeValid, isCampaignExpired }) => {
              this.isCampaignExpired = isCampaignExpired;
              this.isPasscodeInvalid = !isPasscodeValid;

              this.isVerifyingCookie = false;

              if (isCampaignExpired || !isPasscodeValid) this.$store.dispatch("preloader/passcode/REMOVE_COOKIE");
              if (isPasscodeValid) this.$eventBus.$emit('preloader-set-active-component', 'progress-bar');

              resolve(isPasscodeValid);
            })
            .then(this.disableShareRecipe)
            .then(this.disableAddRecipeToFavorites)
            .catch(err => {
              console.error(err);
              reject(err);
            });
        } else {
          this.isVerifyingCookie = false;
          resolve(null);
        }
      });
    },

    validateCampaignId() {
      return new Promise((resolve, reject) => {
        this.isValidatingCampaign = true;
        this.$store
          .dispatch('preloader/passcode/VALIDATE_CAMPAIGN_ID')
          .then(isCampaignValid => {
            this.isCampaignInvalid = !isCampaignValid;
            this.isValidatingCampaign = false;
            resolve(isCampaignValid);
          })
          .catch(err => {
            console.error(err);
            reject(err);
          });
      });
    },

    validatePasscode() {
      this.isValidatingPasscode = true;

      // checks the passcode against the endpoint, sets the flags in the store
      // with the response and returns a boolean with the result
      this.$store
        .dispatch(
          "preloader/passcode/VALIDATE_PASSCODE",
          { passcode: this.passcode, counter: true }
        )
        .then(({ isPasscodeValid, isCampaignExpired }) => {
          this.isCampaignExpired = isCampaignExpired;
          this.isPasscodeInvalid = !isPasscodeValid;

          this.isValidatingPasscode = false;

          if (isPasscodeValid) {
            this.$eventBus.$emit('preloader-set-active-component', 'progress-bar');
            this.$eventBus.$emit('recalculate-customizer-screen-size');
          }
        })
        .then(this.disableShareRecipe)
        .then(this.disableAddRecipeToFavorites)
        .catch(err => {
          this.isValidatingPasscode = false;
          console.error(err);
        });
    },

    disableShareRecipe() {
      this.$store.commit("utils/SET_PROPERTY_CONFIGURATION", { property:  "enableShareYourDesign", value: false });
      const shareEl = document.querySelector('.configurator2-header-share');
      if (shareEl) shareEl.classList.add("hide");
      const shareInStoreEl = document.querySelector('.configurator2-header-share-in-store');
      if (shareInStoreEl) shareInStoreEl.classList.add("hide");
    },

    disableAddRecipeToFavorites() {
      this.$store.commit("utils/SET_PROPERTY_CONFIGURATION", { property:  "enableFavorites", value: false });
      const favoritesEl = document.querySelector('.configurator2-header-save');
      if (favoritesEl) favoritesEl.classList.add("hide");
    },
  },
  created() {
    this.initValidation();
  }
};
</script>
